import { GuestRedeemResponse } from './api-types';

/**
 * Module-scoped, in-memory only (cleared on a full page reload, NEVER persisted to storage —
 * same security profile as the in-memory session). Lets a GuestSessionProvider REMOUNT within
 * one page load reuse the session already minted for this link token instead of firing a fresh
 * redeem. A stray remount — an SW takeover or an extension-induced re-render — would otherwise
 * mint a new session, rotate the token server-side, and 401 the autosave still using the
 * previous one. Keyed by link token so a different grant never reuses a stale session.
 */
let liveSession: { linkToken: string; response: GuestRedeemResponse } | null =
  null;

/** The cached redeem payload for this link token, or null if none/other token. */
export const getCachedSession = (
  linkToken: string
): GuestRedeemResponse | null =>
  liveSession && liveSession.linkToken === linkToken
    ? liveSession.response
    : null;

/** Cache the full redeem payload for a freshly redeemed link token. */
export const setCachedSession = (
  linkToken: string,
  response: GuestRedeemResponse
): void => {
  liveSession = { linkToken, response };
};

/**
 * After a session recovery (re-redeem), refresh only the session token — keep the original
 * seed data so a later remount restores THIS session instead of minting yet another one.
 * Only reuse the cached seed data when it belongs to the SAME token; if the cache holds a
 * different (or no) grant, store the fresh response wholesale so we never combine one grant's
 * seed data with another grant's session token.
 */
export const cacheRecoveredSession = (
  linkToken: string,
  response: GuestRedeemResponse
): void => {
  liveSession =
    liveSession && liveSession.linkToken === linkToken
      ? {
          linkToken,
          response: {
            ...liveSession.response,
            sessionToken: response.sessionToken
          }
        }
      : { linkToken, response };
};

/**
 * The one in-flight redeem for this link token (initial mount or recovery — both are the
 * same POST /redeem). Shared so a provider remount or a concurrent recovery can subscribe
 * to the request already running instead of firing a second redeem: every redeem rotates
 * the session server-side, so a duplicate call invalidates the session the first caller
 * is about to receive.
 */
let redeemInFlight: {
  linkToken: string;
  promise: Promise<GuestRedeemResponse>;
} | null = null;

/** The redeem already in flight for this link token, or null if none/other token. */
export const getPendingRedeem = (
  linkToken: string
): Promise<GuestRedeemResponse> | null =>
  redeemInFlight && redeemInFlight.linkToken === linkToken
    ? redeemInFlight.promise
    : null;

/** Register a just-fired redeem as THE in-flight one; auto-clears when it settles. */
export const trackPendingRedeem = (
  linkToken: string,
  promise: Promise<GuestRedeemResponse>
): void => {
  redeemInFlight = { linkToken, promise };
  const release = () => {
    if (redeemInFlight?.promise === promise) {
      redeemInFlight = null;
    }
  };
  promise.then(release, release);
};

/** Drop the cached session and any in-flight redeem (grant terminal/dead, or test reset). */
export const clearCachedSession = (): void => {
  liveSession = null;
  redeemInFlight = null;
};
